import { useMemo, useState } from "react";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Avatar } from "@/components/Avatar";
import { CreditLedgerSection } from "@/components/admin/CreditLedgerSection";
import { Flame, Ticket, Plus, Minus, UserCog, Percent, Trash2, HeartPulse, ShieldCheck } from "lucide-react";

const STATUS_STYLE = { attended: "text-primary", no_show: "text-destructive", confirmed: "text-muted-foreground", waitlist: "text-muted-foreground", cancelled: "text-muted-foreground" };
const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "America/Panama" }) : "";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", timeZone: "America/Panama" }) : "";

function HealthSection({ user, onValidateHealth }) {
  const { tl } = useI18n();
  const h = user.health || {};
  const FIELDS = [
    ["injuries", { es: "Lesiones", en: "Injuries", fr: "Blessures" }],
    ["pain", { es: "Dolores", en: "Pain", fr: "Douleurs" }],
    ["surgeries", { es: "Operaciones", en: "Surgeries", fr: "Opérations" }],
    ["pregnancy", { es: "Embarazo / posparto", en: "Pregnancy / postpartum", fr: "Grossesse / post-partum" }],
    ["cardiovascular", { es: "Cardiovascular", en: "Cardiovascular", fr: "Cardiovasculaire" }],
    ["medications", { es: "Medicamentos", en: "Medications", fr: "Médicaments" }],
    ["restrictions", { es: "Restricciones médicas", en: "Medical restrictions", fr: "Restrictions médicales" }],
    ["emergency_contact", { es: "Contacto de emergencia", en: "Emergency contact", fr: "Contact d'urgence" }],
  ];
  const flag = user.health_flag;
  const flagStyle = flag === "validated" ? "bg-primary/10 text-primary"
    : flag === "attention" ? "bg-destructive/10 text-destructive" : "bg-secondary text-muted-foreground";
  return (
    <div data-testid="client-health">
      <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><HeartPulse className="h-4 w-4" />{tl({ es: "Cuestionario de salud", en: "Health questionnaire", fr: "Questionnaire de santé" })}</h3>
      {!user.health_completed ? (
        <p className="text-sm text-muted-foreground">{tl({ es: "El cliente aún no completó el cuestionario.", en: "The client hasn't completed the questionnaire yet.", fr: "Le client n'a pas encore rempli le questionnaire." })}</p>
      ) : (
        <div className="space-y-2">
          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${flagStyle}`} data-testid="health-flag">
            {flag === "validated" ? tl({ es: "Validado", en: "Validated", fr: "Validé" })
              : flag === "attention" ? tl({ es: "Requiere atención", en: "Needs attention", fr: "À vérifier" })
              : flag}
          </span>
          <div className="divide-y divide-border/60 rounded-lg border border-border">
            {FIELDS.map(([k, label]) => (
              <div key={k} className="flex gap-3 px-3 py-2 text-sm">
                <span className="w-40 shrink-0 text-muted-foreground">{tl(label)}</span>
                <span className="min-w-0 flex-1 whitespace-pre-wrap">{h[k] ? h[k] : "—"}</span>
              </div>
            ))}
          </div>
          {onValidateHealth && flag !== "validated" && (
            <Button data-testid="validate-health-btn" size="sm" onClick={() => onValidateHealth(user)} className="rounded-full">
              <ShieldCheck className="mr-1.5 h-4 w-4" />
              {tl({ es: "Validar cuestionario", en: "Validate questionnaire", fr: "Valider le questionnaire" })}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}


function GrantPackSection({ credits, subscriptions, products, cardOnFile, onGrant, onAdjust, onCancelSub, onDeletePack }) {
  const { tl } = useI18n();
  const [pid, setPid] = useState("");
  const [method, setMethod] = useState("manual_cash");
  const [busy, setBusy] = useState(false);
  const active = useMemo(() => (credits || []).filter((c) => c.status === "active"), [credits]);
  const activeProducts = useMemo(() => (products || []).filter((p) => p.active), [products]);
  // Map a credit to its active/frozen subscription so we can offer "cancel recurrence".
  const subByCredit = useMemo(() => {
    const m = {};
    (subscriptions || []).forEach((s) => { if (s.credit_id && s.status !== "cancelled") m[s.credit_id] = s; });
    return m;
  }, [subscriptions]);
  const submit = async () => {
    if (!pid) return;
    setBusy(true);
    await onGrant(pid, method);
    setPid(""); setBusy(false);
  };
  return (
    <div data-testid="client-packs">
      <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><Ticket className="h-4 w-4" />{tl({ es: "Forfaits / créditos", en: "Packs / credits", fr: "Forfaits / crédits" })}</h3>
      {active.length === 0 ? (
        <p className="mb-3 text-sm text-muted-foreground">{tl({ es: "Sin créditos activos", en: "No active credits", fr: "Aucun crédit actif" })}</p>
      ) : (
        <div className="mb-3 space-y-1.5">
          {active.map((c) => {
            const unlimited = c.credits_remaining >= 999 || (c.credits_total || 0) >= 999;
            const total = c.credits_total ?? c.credits_remaining;
            const sub = subByCredit[c.credit_id];
            return (
            <div key={c.credit_id} data-testid={`credit-${c.credit_id}`} className="flex items-center justify-between gap-2 rounded-lg bg-secondary/40 px-3 py-2 text-sm">
              <span className="min-w-0 truncate font-medium">
                {c.credit_type}
                {sub && <span className="ml-1.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">{tl({ es: "abono", en: "subscription", fr: "abonnement" })}</span>}
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {unlimited ? "∞" : (
                    <span data-testid={`credit-usage-${c.credit_id}`}><span className="font-semibold text-foreground">{c.credits_remaining}/{total}</span> {tl({ es: "restantes", en: "left", fr: "restantes" })}</span>
                  )}
                  {" · "}{tl({ es: "vence", en: "exp.", fr: "exp." })} {fmtDate(c.expires_at)}
                </span>
                {onAdjust && c.credits_remaining < 999 && (
                  <span className="flex items-center gap-1">
                    <button data-testid={`credit-minus-${c.credit_id}`} onClick={() => onAdjust(c.credit_id, -1)} className="grid h-6 w-6 place-items-center rounded-full bg-background hover:bg-destructive/10 hover:text-destructive" title={tl({ es: "Quitar 1 sesión", en: "Debit 1 session", fr: "Débiter 1 séance" })}><Minus className="h-3.5 w-3.5" /></button>
                    <button data-testid={`credit-plus-${c.credit_id}`} onClick={() => onAdjust(c.credit_id, 1)} className="grid h-6 w-6 place-items-center rounded-full bg-background hover:bg-primary/10 hover:text-primary" title={tl({ es: "Añadir 1 sesión", en: "Add 1 session", fr: "Créditer 1 séance" })}><Plus className="h-3.5 w-3.5" /></button>
                  </span>
                )}
                {sub && onCancelSub && (
                  <button data-testid={`sub-cancel-${sub.subscription_id}`} onClick={() => onCancelSub(sub)}
                    className="grid h-6 w-6 place-items-center rounded-full bg-background text-destructive hover:bg-destructive/10"
                    title={tl({ es: "Cancelar la renovación del abono", en: "Cancel subscription renewal", fr: "Annuler le renouvellement de l'abonnement" })}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
                {!sub && onDeletePack && (
                  <button data-testid={`credit-delete-${c.credit_id}`} onClick={() => onDeletePack(c)}
                    className="grid h-6 w-6 place-items-center rounded-full bg-background text-destructive hover:bg-destructive/10"
                    title={tl({ es: "Eliminar este forfait", en: "Delete this pack", fr: "Supprimer ce forfait" })}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            </div>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-border p-3">
        <div className="min-w-[180px] flex-1">
          <label className="mb-1 block text-xs text-muted-foreground">{tl({ es: "Añadir forfait", en: "Add pack", fr: "Ajouter un forfait" })}</label>
          <select data-testid="grant-product" value={pid} onChange={(e) => setPid(e.target.value)} className="h-10 w-full rounded-lg border border-border bg-background px-2 text-sm">
            <option value="">—</option>
            {activeProducts.map((p) => (
              <option key={p.product_id} value={p.product_id}>{tl(p.name)} · {p.credit_type} · ${p.price}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">{tl({ es: "Método", en: "Method", fr: "Méthode" })}</label>
          <select data-testid="grant-method" value={method} onChange={(e) => setMethod(e.target.value)} className="h-10 rounded-lg border border-border bg-background px-2 text-sm">
            <option value="manual_cash">{tl({ es: "Efectivo", en: "Cash", fr: "Espèces" })}</option>
            <option value="manual_transfer">{tl({ es: "Transferencia", en: "Transfer", fr: "Virement" })}</option>
            <option value="manual_card">{tl({ es: "Tarjeta (en el estudio)", en: "Card (in studio)", fr: "Carte (au studio)" })}</option>
            {cardOnFile && <option value="saved_card" data-testid="grant-method-saved-card">{tl({ es: "Cobrar tarjeta guardada (online)", en: "Charge saved card (online)", fr: "Débiter la carte enregistrée (en ligne)" })}</option>}
            <option value="yappy">Yappy</option>
            <option value="comp">{tl({ es: "Cortesía (gratis)", en: "Courtesy (free)", fr: "Offert (gratuit)" })}</option>
          </select>
        </div>
        <Button data-testid="grant-pack-btn" size="sm" disabled={!pid || busy} onClick={submit} className="h-10 rounded-full">
          {method === "saved_card" ? tl({ es: "Cobrar y añadir", en: "Charge & add", fr: "Débiter et ajouter" }) : tl({ es: "Añadir", en: "Add", fr: "Ajouter" })}
        </Button>
      </div>
    </div>
  );
}

function RoleSection({ user, onSetRole }) {
  const { tl } = useI18n();
  const [uni, setUni] = useState("fitness");
  if (!user) return null;
  const isCoach = user.role === "coach";
  return (
    <div data-testid="client-role" className="flex flex-wrap items-center gap-2 rounded-xl border border-border p-3">
      <div className="flex items-center gap-1.5 text-sm font-semibold">
        <UserCog className="h-4 w-4" />{tl({ es: "Rol", en: "Role", fr: "Rôle" })}: <span className="font-normal capitalize">{user.role}</span>
      </div>
      <div className="flex-1" />
      {isCoach ? (
        <Button data-testid="demote-btn" size="sm" variant="outline" onClick={() => onSetRole("client")} className="rounded-full text-destructive">
          {tl({ es: "Volver a cliente", en: "Back to client", fr: "Repasser en client" })}
        </Button>
      ) : (
        <>
          <select data-testid="coach-universe" value={uni} onChange={(e) => setUni(e.target.value)} className="h-9 rounded-lg border border-border bg-background px-2 text-sm">
            <option value="fitness">Fitness</option>
            <option value="pilates">Pilates</option>
            <option value="both">Fitness + Pilates</option>
          </select>
          <Button data-testid="promote-btn" size="sm" onClick={() => onSetRole("coach", uni)} className="rounded-full">
            {tl({ es: "Convertir en coach", en: "Make coach", fr: "Convertir en coach" })}
          </Button>
        </>
      )}
    </div>
  );
}

function DiscountSection({ user, onSetDiscount }) {
  const { tl } = useI18n();
  const [pct, setPct] = useState(user?.discount_percent ?? 0);
  const [busy, setBusy] = useState(false);
  if (!user) return null;
  const save = async () => {
    setBusy(true);
    await onSetDiscount(Number(pct) || 0);
    setBusy(false);
  };
  const current = Number(user.discount_percent) || 0;
  return (
    <div data-testid="client-discount" className="rounded-xl border border-border p-3">
      <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
        <Percent className="h-4 w-4" />{tl({ es: "Descuento personalizado", en: "Personalized discount", fr: "Remise personnalisée" })}
      </h3>
      <p className="mb-2 text-xs text-muted-foreground">
        {tl({ es: "Se aplica a todas las ofertas de este cliente al pagar. Solo visible para ti y el cliente.",
              en: "Applied to all this client's offers at checkout. Only visible to you and the client.",
              fr: "Appliquée à toutes les offres de ce client au paiement. Visible uniquement par vous et le client." })}
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">%</label>
          <Input data-testid="discount-input" type="number" min="0" max="100" step="1" value={pct}
            onChange={(e) => setPct(e.target.value)} placeholder="0" className="h-10 w-28" />
        </div>
        <Button data-testid="discount-save" size="sm" disabled={busy} onClick={save} className="h-10 rounded-full">
          {tl({ es: "Guardar", en: "Save", fr: "Enregistrer" })}
        </Button>
        {current > 0 && (
          <span data-testid="discount-current" className="ml-auto rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
            −{current}% {tl({ es: "activo", en: "active", fr: "actif" })}
          </span>
        )}
      </div>
    </div>
  );
}

export function ClientDetailDialog({ detail, onClose, goalW, setGoalW, goalS, setGoalS, saveGoals, products, onGrant, onAdjust, onSetRole, onSetDiscount, onCancelSub, onDelete, onValidateHealth, onDeletePack }) {
  const { t, tl } = useI18n();
  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="client-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar userId={detail?.user?.user_id} name={detail?.name} size={40} updatedAt={detail?.user?.avatar_updated_at} />{detail?.name}
          </DialogTitle>
          <DialogDescription>{tl({ es: "Historial de asistencia del cliente", en: "Client attendance history", fr: "Historique de fréquentation du client" })}</DialogDescription>
        </DialogHeader>
        {detail?.loading ? (
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        ) : detail ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm font-semibold text-primary">
              <Flame className="h-4 w-4" /> {detail.attended_days || 0} {tl({ es: "días asistidos", en: "days attended", fr: "jours de présence" })}
            </div>
            <div>
              <h3 className="mb-2 text-sm font-semibold">{tl({ es: "Historial de asistencia", en: "Attendance history", fr: "Historique de fréquentation" })}</h3>
              {(!detail.attendance || detail.attendance.length === 0) ? (
                <p className="text-sm text-muted-foreground">{t("common.noData")}</p>
              ) : (
                <div className="space-y-1.5">
                  {detail.attendance.map((a) => (
                    <div key={a.reservation_id} data-testid={`attendance-${a.reservation_id}`} className="flex items-center justify-between rounded-lg bg-secondary/40 px-3 py-2 text-sm">
                      <span>{a.title ? (a.title.fr || a.title.es || a.title.en) : a.universe} <span className="text-xs text-muted-foreground">· {fmt(a.start)}</span></span>
                      <span className={`text-xs font-semibold ${STATUS_STYLE[a.status] || "text-muted-foreground"}`}>{a.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {(detail.measurements?.length > 0 || detail.steps_recent?.length > 0) && (
              <div data-testid="client-tracking">
                <h3 className="mb-2 text-sm font-semibold">{tl({ es: "Seguimiento físico", en: "Physical tracking", fr: "Suivi physique" })}</h3>
                {detail.measurements?.length > 0 && (
                  <div className="mb-2 rounded-lg bg-secondary/40 px-3 py-2 text-sm">
                    <span className="font-semibold">{tl({ es: "Peso", en: "Weight", fr: "Poids" })}: </span>
                    {detail.measurements[detail.measurements.length - 1].weight} kg
                    <span className="text-xs text-muted-foreground"> · {fmt(detail.measurements[detail.measurements.length - 1].date)}</span>
                    <span className="ml-2 text-xs text-muted-foreground">({detail.measurements.length} {tl({ es: "registros", en: "entries", fr: "relevés" })})</span>
                  </div>
                )}
                {detail.steps_recent?.length > 0 && (
                  <div className="rounded-lg bg-secondary/40 px-3 py-2 text-sm">
                    <span className="font-semibold">{tl({ es: "Pasos recientes", en: "Recent steps", fr: "Pas récents" })}: </span>
                    {detail.steps_recent.slice(0, 5).map((x) => `${x.date.slice(5)}: ${x.steps.toLocaleString()}`).join(" · ")}
                  </div>
                )}
              </div>
            )}
            {onSetRole && <RoleSection user={detail.user} onSetRole={onSetRole} />}
            {onSetDiscount && <DiscountSection user={detail.user} onSetDiscount={onSetDiscount} />}
            {onGrant && <GrantPackSection credits={detail.credits} subscriptions={detail.subscriptions} products={products} cardOnFile={detail.card_on_file} onGrant={onGrant} onAdjust={onAdjust} onCancelSub={onCancelSub} onDeletePack={onDeletePack} />}
            {onGrant && <CreditLedgerSection userId={detail.user.user_id} />}
            <HealthSection user={detail.user} onValidateHealth={onValidateHealth} />
            <div data-testid="client-goals">
              <h3 className="mb-2 text-sm font-semibold">{tl({ es: "Objetivos del cliente", en: "Client goals", fr: "Objectifs du client" })}</h3>
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">{tl({ es: "Peso objetivo (kg)", en: "Target weight (kg)", fr: "Poids cible (kg)" })}</label>
                  <Input data-testid="goal-weight-input" type="number" step="0.1" value={goalW} onChange={(e) => setGoalW(e.target.value)} placeholder="68" className="h-10 w-32" />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">{tl({ es: "Pasos/día", en: "Steps/day", fr: "Pas/jour" })}</label>
                  <Input data-testid="goal-steps-input" type="number" value={goalS} onChange={(e) => setGoalS(e.target.value)} placeholder="10000" className="h-10 w-32" />
                </div>
                <Button data-testid="goal-save" size="sm" onClick={saveGoals} className="h-10 rounded-full">{tl({ es: "Guardar", en: "Save", fr: "Enregistrer" })}</Button>
              </div>
            </div>
            {onDelete && (
              <div className="mt-2 border-t border-border pt-4">
                <h3 className="mb-2 text-sm font-semibold text-destructive">{tl({ es: "Zona peligrosa", en: "Danger zone", fr: "Zone sensible" })}</h3>
                <p className="mb-2 text-xs text-muted-foreground">
                  {tl({
                    es: "Elimina definitivamente esta cuenta y sus datos (reservas, créditos, suscripciones). El historial de pagos se conserva.",
                    en: "Permanently deletes this account and its data (bookings, credits, subscriptions). Payment history is kept.",
                    fr: "Supprime définitivement ce compte et ses données (réservations, crédits, abonnements). L'historique des paiements est conservé.",
                  })}
                </p>
                <Button data-testid="delete-client-btn" variant="outline" size="sm"
                  onClick={() => onDelete(detail.user)}
                  className="rounded-full border-destructive text-destructive hover:bg-destructive/10">
                  <Trash2 className="mr-1.5 h-4 w-4" />
                  {tl({ es: "Eliminar cliente", en: "Delete client", fr: "Supprimer le client" })}
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
